import type { CohortPromptFields } from '@/lib/cohorts.ts';
import type { CohortPrompt } from '@/types/cohorts.ts';

/** The four lists, in the order they are compiled into the system prompt. */
export const PROMPT_FIELDS = [
  {
    key: 'purpose',
    title: 'Purpose',
    placeholder: 'Add purpose information...',
    addButtonText: 'Add Purpose',
  },
  {
    key: 'audience',
    title: 'Audience',
    placeholder: 'Add audience information...',
    addButtonText: 'Add Audience',
  },
  {
    key: 'guardrails',
    title: 'Guardrails',
    placeholder: 'Add a behavioral guideline...',
    addButtonText: 'Add Guardrail',
  },
  {
    key: 'examples',
    title: 'Examples',
    placeholder: 'Add an example...',
    addButtonText: 'Add Example',
  },
] as const;

export type PromptField = (typeof PROMPT_FIELDS)[number];

export type PromptFieldKey = PromptField['key'];

export const EMPTY_PROMPT_FIELDS: CohortPromptFields = {
  purpose: [],
  audience: [],
  guardrails: [],
  examples: [],
};

export function promptFieldsOf(prompt: CohortPrompt | null): CohortPromptFields {
  if (!prompt) return EMPTY_PROMPT_FIELDS;
  return {
    purpose: [...(prompt.purpose ?? [])],
    audience: [...(prompt.audience ?? [])],
    guardrails: [...(prompt.guardrails ?? [])],
    examples: [...(prompt.examples ?? [])],
  };
}

/** What the four lists become where they are used: one block per non-empty list. */
export function compileSystemPrompt(fields: CohortPromptFields): string {
  const sections = PROMPT_FIELDS.filter((field) => fields[field.key].length > 0).map(
    (field) =>
      `# ${field.title}:\n\n${fields[field.key].map((item) => `- ${item}`).join('\n')}`,
  );

  return sections.length > 0
    ? sections.join('\n\n')
    : 'No prompt configuration yet. Add purpose, audience, guardrails, or examples to see the compiled system prompt.';
}
